import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { BehaviorSubject, Observable } from 'rxjs';
import * as xlsx from "xlsx-js-style";
declare const require: any;
const jsPDF = require("jspdf");
require("jspdf-autotable");
const FileSaver = require("file-saver");

@Injectable({
    providedIn: "root",
})
export class ActivityService {
    constructor() {}

    viewSelected = new BehaviorSubject<{ type: string; date: Date }>({ type: "day", date: new Date() });
    seeAll = false;
    userId:number;
    tenantId:number;
    getMonthViewSelectedAsObs(): Observable<{ type: string; date: Date }> {
        return this.viewSelected.asObservable();
    }

    selectView(view: string, date?: Date): void {
        const normalizedDate = date !== undefined
            ? new Date(date.getFullYear(), date.getMonth(), date.getDate())
            : date;
        if (view === "day" && normalizedDate !== undefined) {
            this.viewSelected.next({ type: view, date: normalizedDate });
        }
        if (view !== "day") {
            this.viewSelected.next({ type: view, date: normalizedDate });
        }
    }

    setZeroTimeForLocalDate(date: Date): Date {
        date.setHours(0, 0, 0, 0);
        return new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);
    }

    toApiCalendarDate(date: Date = new Date()): DateTime {
        const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const utcMidnight = this.setZeroTimeForLocalDate(localDate);
        return DateTime.utc(utcMidnight.getUTCFullYear(), utcMidnight.getUTCMonth() + 1, utcMidnight.getUTCDate());
    }

    // get local js date from a UTC datetime object
    getLocalJsDate(date: DateTime): Date {
        let jsDate = new Date(
            `${date?.year}-${date?.month < 10 ? "0" + date?.month : date?.month}-${date?.day < 10 ? "0" + date?.day : date?.day}T${
                date?.hour < 10 ? "0" + date?.hour : date?.hour
            }:${date?.minute < 10 ? "0" + date?.minute : date?.minute}:${date?.second < 10 ? "0" + date?.second : date?.second}`
        );
        return new Date(jsDate.getTime() - jsDate.getTimezoneOffset() * 60 * 1000);
    }

    timestampToMinutes(timestamp: string): number {
        if (!timestamp) return 0;
        return parseInt(timestamp.split(":")[0]) * 60 + parseInt(timestamp.split(":")[1]);
    }

    minutesToTimestamp(minutes: number): string {
        let hours = Math.floor(minutes / 60);
        let min = minutes % 60;
        if (isNaN(hours) || isNaN(min)) return "--:--";
        return `${hours < 10 ? "0" + hours : hours}:${min < 10 ? "0" + min : min}`;
    }

    private timeSpanToSeconds(timestamp: string | undefined): number {
        if (!timestamp) {
            return 0;
        }
        const parts = timestamp.split(":").map((part) => parseInt(part, 10));
        if (parts.some((part) => Number.isNaN(part))) {
            return 0;
        }
        return (parts[0] || 0) * 3600 + (parts[1] || 0) * 60 + (parts[2] || 0);
    }

    private secondsToTimeSpan(totalSeconds: number): string {
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = Math.floor(totalSeconds % 60);
        return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    }

    /** Aggregate daily activity calculations into week/month summary totals and averages. */
    aggregatePeriodTimeAndAttendance<T extends {
        productivityTime?: string;
        trackedTime?: string;
        timeAtWork?: string;
        effectiveness?: number;
        productivity?: number;
        productivityPercentage?: number;
        rank?: number;
        minimumUsererWorkingHours?: number;
        arrivalTime?: { hour?: number; minute?: number };
        leftDateTime?: unknown;
        leftTime?: string;
        workStartDate?: unknown;
        workEndDate?: unknown;
        productivityBarStartTime?: unknown;
        isPRodTimePresent?: boolean;
    }>(dailyResults: T[]): T & {
        avarageProductivityTime: string;
        avarageTrackedTime: string;
        avarageTimeAtWork: string;
    } {
        const daysWithActivity = dailyResults.filter(
            (day) =>
                this.timeSpanToSeconds(day?.productivityTime) > 0 ||
                this.timeSpanToSeconds(day?.trackedTime) > 0 ||
                this.timeSpanToSeconds(day?.timeAtWork) > 0
        );
        const base = (daysWithActivity.length ? daysWithActivity[daysWithActivity.length - 1] : dailyResults[0]) ?? ({} as T);

        let totalProductivity = 0;
        let totalTracked = 0;
        let totalTimeAtWork = 0;
        daysWithActivity.forEach((day) => {
            totalProductivity += this.timeSpanToSeconds(day.productivityTime);
            totalTracked += this.timeSpanToSeconds(day.trackedTime);
            totalTimeAtWork += this.timeSpanToSeconds(day.timeAtWork);
        });

        const divisor = Math.max(daysWithActivity.length, 1);
        const aggregated = {
            ...base,
            productivityTime: this.secondsToTimeSpan(totalProductivity),
            trackedTime: this.secondsToTimeSpan(totalTracked),
            timeAtWork: this.secondsToTimeSpan(totalTimeAtWork),
            effectiveness: daysWithActivity.reduce((max, day) => Math.max(max, day.effectiveness ?? 0), 0),
            productivity: daysWithActivity.reduce((max, day) => Math.max(max, day.productivity ?? 0), 0),
            productivityPercentage: daysWithActivity.reduce((max, day) => Math.max(max, day.productivityPercentage ?? 0), 0),
            rank: daysWithActivity.reduce((max, day) => Math.max(max, day.rank ?? 0), 0),
            avarageProductivityTime: this.secondsToTimeSpan(totalProductivity / divisor),
            avarageTrackedTime: this.secondsToTimeSpan(totalTracked / divisor),
            avarageTimeAtWork: this.secondsToTimeSpan(totalTimeAtWork / divisor),
        };

        return aggregated as T & {
            avarageProductivityTime: string;
            avarageTrackedTime: string;
            avarageTimeAtWork: string;
        };
    }

    getHoursAndMin(timespan: string) {
        if (!timespan) return "0 hrs 0 min";
        let hours = parseInt(timespan.split(":")[0]);
        let min = parseInt(timespan.split(":")[1]);
        return `${hours} hrs ${min} min`;
    }

    exportPdf(exportColumns: any[], data: any, name: string, pdfTitle: string) {
        const doc = new jsPDF.default("l", "pt");
        doc.setTextColor(100);
        doc.text(40, 40, pdfTitle);
        const tableData = data.map((item) => exportColumns.map((column) => item[column.dataKey]));
        doc.autoTable({
            head: [exportColumns.map((column) => column.title)],
            body: tableData,
            startY: 60,
        });
        doc.save(`${name}.pdf`);
    }

    exportExcel(columns: string[][], data: any[][], sheetName: string, fileName: string) {
        const workbook: xlsx.WorkBook = xlsx.utils.book_new();

       
        const worksheet = xlsx.utils.aoa_to_sheet([]);
        xlsx.utils.sheet_add_aoa(worksheet, [[fileName]], { origin: "A1" });
        xlsx.utils.sheet_add_aoa(worksheet, columns, {
            origin: "A2",
        });
        xlsx.utils.sheet_add_aoa(worksheet, data, {
            origin: "A3",
        });
           // Merge and center cells A1 to F1
worksheet["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }];
        this.styleExcel(worksheet);
        xlsx.utils.book_append_sheet(workbook, worksheet, sheetName);
        const excelBuffer: any = xlsx.write(workbook, {
            bookType: "xlsx",
            type: "array",
        });
        this.saveAsExcelFile(excelBuffer, fileName);
    }

    styleExcel(ws: xlsx.WorkSheet) {
        ws["!cols"] = new Array(4).fill({ width: 20 });
        for (let i in ws) {
            if (typeof ws[i] != "object") continue;
            let cell = xlsx.utils.decode_cell(i);

            ws[i].s = {
                font: {
                    name: "Calibri",
                    sz: 11,
                    color: { rgb: "000000" },
                },
            };

            ws[i].s.border = {
                top: {
                    style: "thin",
                    color: { rgb: "000000" },
                },
                bottom: {
                    style: "thin",
                    color: { rgb: "000000" },
                },
                left: {
                    style: "thin",
                    color: { rgb: "000000" },
                },
                right: {
                    style: "thin",
                    color: { rgb: "000000" },
                },
            };

            if (cell.r == 1) {
                ws[i].s.font.bold = true;
                ws[i].s.fill = {
                    patternType: "solid",
                    fgColor: { rgb: "DDDDDD" },
                };
            }
            if (cell.r == 0) {
                ws[i].s.font.bold = true;
                ws[i].s.font.underline = true; 
                ws[i].s.font.sz = 12;
                ws[i].s.fill = {
                    patternType: "solid",
                    fgColor: { rgb: "FFFFFF" },

                };
                ws[i].s.alignment = {
                    vertical: "center",
                        horizontal: "center",
                        wrapText: '1',
                }
            }
        }
    }

    saveAsExcelFile(buffer: any, fileName: string): void {
        let EXCEL_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        let EXCEL_EXTENSION = ".xlsx";
        const data: Blob = new Blob([buffer], {
            type: EXCEL_TYPE,
        });
        FileSaver.saveAs(data, fileName + EXCEL_EXTENSION);
    }
}
